import { useEffect, useState } from "react";
import { Gift, Copy, Check, Send } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";


export default function ReferralsPage() {
    const [data, setData] = useState(null);
    const [code, setCode] = useState("");
    const [applying, setApplying] = useState(false);
    const [copied, setCopied] = useState(false);

    const refresh = () => api.get("/referrals/me").then(({ data }) => setData(data)).catch(() => {});
    useEffect(() => { refresh(); }, []);

    const shareLink = data ? `${window.location.origin}/auth?ref=${data.code}` : "";

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
            toast.success("Link copied");
        } catch {
            toast.error("Couldn't copy — long-press to copy manually");
        }
    };

    const share = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "Try SMART Jamaica",
                    text: `Use my code ${data.code} on SMART Jamaica — we both get JMD$${(data.credit_per_friend_jmd).toLocaleString()} off our next booking.`,
                    url: shareLink,
                });
            } catch {/* user cancelled */}
        } else {
            copy();
        }
    };

    const apply = async () => {
        if (!code.trim()) return toast.error("Enter a code");
        setApplying(true);
        try {
            const { data: res } = await api.post("/referrals/apply", { code: code.toUpperCase().trim() });
            toast.success(res.message);
            setCode("");
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 max-w-3xl mx-auto w-full px-6 sm:px-10 py-16">
                <div className="overline mb-3">Refer & Earn</div>
                <h1 className="font-serif text-4xl sm:text-5xl mb-4">
                    Give <span className="gold-text">JMD${data?.credit_per_friend_jmd?.toLocaleString() || "1,500"}</span>, get <span className="gold-text">JMD${data?.credit_per_friend_jmd?.toLocaleString() || "1,500"}</span>
                </h1>
                <p className="text-sm text-muted-foreground max-w-xl mb-12 leading-relaxed">
                    Share your code. Your friend gets credit on their first booking — and you get the same credit dropped into your account the moment they pay.
                </p>

                {data && (
                    <>
                        <div className="p-6 sm:p-8 bg-gradient-to-br from-primary/15 via-card to-card border border-primary/40 rounded-sm mb-10 relative overflow-hidden" data-testid="referral-card">
                            <div className="absolute -right-10 -top-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
                            <div className="flex items-center gap-2 mb-4 relative">
                                <Gift size={14} className="gold-text" strokeWidth={1.5} />
                                <div className="overline">Your code</div>
                            </div>
                            <div className="flex items-baseline gap-3 mb-6 relative">
                                <span className="font-serif text-5xl gold-text tracking-wider" data-testid="my-referral-code">{data.code}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 relative">
                                <Button
                                    onClick={copy}
                                    className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11"
                                    data-testid="copy-referral-link"
                                >
                                    {copied ? <><Check size={14} className="mr-2" /> Copied</> : <><Copy size={14} className="mr-2" /> Copy link</>}
                                </Button>
                                <Button
                                    onClick={share}
                                    variant="outline"
                                    className="border-primary/40 gold-text hover:bg-primary/5 rounded-sm uppercase tracking-[0.2em] text-xs h-11"
                                    data-testid="share-referral"
                                >
                                    <Send size={14} className="mr-2" /> Share
                                </Button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-12">
                            <Stat label="Friends referred" value={data.referred_friends || 0} />
                            <Stat label="Credit balance (JMD)" value={`$${(data.credit_balance_jmd || 0).toLocaleString()}`} />
                        </div>

                        <div className="p-6 bg-card border border-white/10 rounded-sm" data-testid="apply-referral-card">
                            <div className="overline mb-2">Got a friend's code?</div>
                            <p className="text-xs text-muted-foreground mb-4">
                                Apply it once — credit lands when you complete your first paid booking.
                            </p>
                            <div className="flex gap-2">
                                <Input
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    placeholder="e.g. SM4RT24"
                                    className="bg-input border-white/10 h-11 rounded-sm flex-1 uppercase tracking-wider"
                                    data-testid="apply-referral-input"
                                />
                                <Button
                                    onClick={apply}
                                    disabled={applying || !code.trim()}
                                    className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-11 px-6"
                                    data-testid="apply-referral-btn"
                                >
                                    {applying ? "Applying…" : "Apply"}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </main>
            <Footer />
        </div>
    );
}


function Stat({ label, value }) {
    return (
        <div className="p-5 bg-card border border-white/10 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{label}</div>
            <div className="font-serif text-3xl gold-text">{value}</div>
        </div>
    );
}
